import { useState, useEffect } from 'react'
import {
  Clock,
  Link2,
  Copy,
  Check,
  ExternalLink,
  Plus,
  Trash2,
  Save,
  Globe,
  Calendar,
  Video,
  AlertCircle,
  Loader2,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'
import { useAuth } from '@/lib/auth/context'
import {
  useTeamAvailability,
  useAvailabilityOverrides,
  useAvailabilityMutations,
} from '@/lib/data/hooks/useAvailability'
import {
  useBookingPages,
  useBookingPageMutations,
} from '@/lib/data/hooks/useBooking'
import type { OverrideType, CreateBookingPageInput } from '@/types/calendar'

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
]

const TIME_OPTIONS = Array.from({ length: 24 * 2 }, (_, i) => {
  const hour = Math.floor(i / 2)
  const minute = i % 2 === 0 ? '00' : '30'
  const time = `${hour.toString().padStart(2, '0')}:${minute}`
  const label = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return { value: time, label }
})

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Dubai',
  'Australia/Sydney',
  'UTC',
]

export function PublicAvailabilityView() {
  const { adminUser } = useAuth()
  const { availability, isLoading: loadingAvailability, refetch } = useTeamAvailability(adminUser?.id)

  // Get overrides for next 90 days
  const today = new Date()
  const startDate = today.toISOString().split('T')[0]
  const endDate = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

  const { overrides, refetch: refetchOverrides } = useAvailabilityOverrides(adminUser?.id, startDate, endDate)
  const { setAvailability, setDefaultAvailability, removeAvailability, addOverride, removeOverride, isLoading: saving } = useAvailabilityMutations()

  const { bookingPages, isLoading: loadingPages, refetch: refetchPages } = useBookingPages()
  const { createBookingPage, updateBookingPage, toggleBookingPage, deleteBookingPage, isLoading: savingPage } = useBookingPageMutations()

  const [localAvailability, setLocalAvailability] = useState<Record<number, { enabled: boolean; startTime: string; endTime: string }>>({})
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone)
  const [hasChanges, setHasChanges] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [copiedLink, setCopiedLink] = useState<string | null>(null)

  // Override form state
  const [showOverrideForm, setShowOverrideForm] = useState(false)
  const [overrideDate, setOverrideDate] = useState('')
  const [overrideType, setOverrideType] = useState<OverrideType>('unavailable')
  const [overrideStartTime, setOverrideStartTime] = useState('09:00')
  const [overrideEndTime, setOverrideEndTime] = useState('17:00')
  const [overrideReason, setOverrideReason] = useState('')

  // Booking page form
  const [showBookingForm, setShowBookingForm] = useState(false)
  const [bookingSlug, setBookingSlug] = useState('')
  const [bookingTitle, setBookingTitle] = useState('Book a Meeting')
  const [bookingDescription, setBookingDescription] = useState('')
  const [bookingDuration, setBookingDuration] = useState(30)

  // Initialize local state from fetched availability
  useEffect(() => {
    if (availability.length > 0) {
      const byDay: Record<number, { enabled: boolean; startTime: string; endTime: string }> = {}

      DAYS_OF_WEEK.forEach(day => {
        byDay[day.value] = { enabled: false, startTime: '09:00', endTime: '17:00' }
      })

      availability.forEach(a => {
        byDay[a.dayOfWeek] = {
          enabled: a.isAvailable,
          startTime: a.startTime,
          endTime: a.endTime,
        }
        if (a.timezone) {
          setTimezone(a.timezone)
        }
      })

      setLocalAvailability(byDay)
    } else {
      const defaultState: Record<number, { enabled: boolean; startTime: string; endTime: string }> = {}
      DAYS_OF_WEEK.forEach(day => {
        defaultState[day.value] = {
          enabled: day.value >= 1 && day.value <= 5,
          startTime: '09:00',
          endTime: '17:00',
        }
      })
      setLocalAvailability(defaultState)
    }
  }, [availability])

  // Generate default slug from user name
  useEffect(() => {
    if (adminUser && !bookingSlug) {
      const name = `${adminUser.firstName || ''} ${adminUser.lastName || ''}`.trim() || adminUser.email.split('@')[0]
      setBookingSlug(name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))
    }
  }, [adminUser, bookingSlug])

  const handleDayToggle = (day: number) => {
    setLocalAvailability(prev => ({
      ...prev,
      [day]: { ...prev[day], enabled: !prev[day]?.enabled },
    }))
    setHasChanges(true)
  }

  const handleTimeChange = (day: number, field: 'startTime' | 'endTime', value: string) => {
    setLocalAvailability(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }))
    setHasChanges(true)
  }

  const handleSaveAvailability = async () => {
    setSaveSuccess(false)

    for (const day of DAYS_OF_WEEK) {
      const dayData = localAvailability[day.value]
      if (!dayData) continue

      if (dayData.enabled) {
        await setAvailability({
          dayOfWeek: day.value,
          startTime: dayData.startTime,
          endTime: dayData.endTime,
          isAvailable: true,
          timezone,
        })
      } else {
        await removeAvailability(day.value)
      }
    }

    await refetch()
    setHasChanges(false)
    setSaveSuccess(true)
    setTimeout(() => setSaveSuccess(false), 3000)
  }

  const handleAddOverride = async () => {
    if (!overrideDate) return

    await addOverride({
      overrideDate,
      overrideType,
      startTime: overrideType === 'custom' ? overrideStartTime : undefined,
      endTime: overrideType === 'custom' ? overrideEndTime : undefined,
      reason: overrideReason || undefined,
    })

    await refetchOverrides()
    setShowOverrideForm(false)
    setOverrideDate('')
    setOverrideReason('')
  }

  const handleCreateBookingPage = async () => {
    if (!bookingSlug) return

    const result = await createBookingPage({
      slug: bookingSlug,
      title: bookingTitle,
      description: bookingDescription || undefined,
      defaultDuration: bookingDuration,
      allowVideoConference: true,
    })

    if (result) {
      await refetchPages()
      setShowBookingForm(false)
      setBookingSlug('')
      setBookingTitle('Book a Meeting')
      setBookingDescription('')
    }
  }

  const getPublicLink = (slug: string) => {
    return `${window.location.origin}/book/${slug}`
  }

  const copyLink = async (slug: string) => {
    const link = getPublicLink(slug)
    await navigator.clipboard.writeText(link)
    setCopiedLink(slug)
    setTimeout(() => setCopiedLink(null), 2000)
  }

  const activeBookingPage = bookingPages.find(p => p.isActive)

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto py-6 px-4 space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-main)]">Public Availability</h1>
          <p className="text-[var(--text-muted)] mt-1">
            Set your available hours and share your booking link
          </p>
        </div>

        {/* Quick Link Section */}
        {activeBookingPage && (
          <div className="bg-gradient-to-r from-[var(--primary)]/10 to-[var(--primary)]/5 border border-[var(--primary)]/20 rounded-xl p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Link2 className="w-5 h-5 text-[var(--primary)]" />
                  <h2 className="text-lg font-semibold text-[var(--text-main)]">Your Booking Link</h2>
                </div>
                <p className="text-[var(--text-muted)] text-sm mb-3">
                  Share this link to let others book time with you
                </p>
                <div className="flex items-center gap-2 bg-[var(--bg-card)] rounded-lg p-3 border border-[var(--border)]">
                  <code className="text-sm text-[var(--primary)] truncate flex-1">
                    {getPublicLink(activeBookingPage.slug)}
                  </code>
                  <button
                    onClick={() => copyLink(activeBookingPage.slug)}
                    className="p-2 hover:bg-[var(--bg-element)] rounded-lg transition-colors flex-shrink-0"
                    title="Copy link"
                  >
                    {copiedLink === activeBookingPage.slug ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4 text-[var(--text-muted)]" />
                    )}
                  </button>
                  <a
                    href={getPublicLink(activeBookingPage.slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-[var(--bg-element)] rounded-lg transition-colors flex-shrink-0"
                    title="Open in new tab"
                  >
                    <ExternalLink className="w-4 h-4 text-[var(--text-muted)]" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Weekly Availability */}
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[var(--primary)]/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-[var(--primary)]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[var(--text-main)]">Weekly Schedule</h2>
                <p className="text-sm text-[var(--text-muted)]">Set your regular available hours</p>
              </div>
            </div>
            <button
              onClick={handleSaveAvailability}
              disabled={!hasChanges || saving}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                hasChanges
                  ? 'bg-[var(--primary)] text-white hover:opacity-90'
                  : 'bg-[var(--bg-element)] text-[var(--text-muted)]'
              } disabled:opacity-50`}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : saveSuccess ? (
                <Check className="w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {saveSuccess ? 'Saved!' : 'Save Changes'}
            </button>
          </div>

          {/* Timezone */}
          <div className="mb-6 pb-6 border-b border-[var(--border)]">
            <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-main)] mb-2">
              <Globe className="w-4 h-4" />
              Timezone
            </label>
            <select
              value={timezone}
              onChange={(e) => {
                setTimezone(e.target.value)
                setHasChanges(true)
              }}
              className="w-full max-w-xs px-3 py-2 bg-[var(--bg-main)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm"
            >
              {TIMEZONES.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>

          {/* Days Grid */}
          <div className="space-y-3">
            {DAYS_OF_WEEK.map(day => {
              const dayData = localAvailability[day.value] || { enabled: false, startTime: '09:00', endTime: '17:00' }
              return (
                <div
                  key={day.value}
                  className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${
                    dayData.enabled ? 'bg-[var(--bg-main)]' : ''
                  }`}
                >
                  <button
                    onClick={() => handleDayToggle(day.value)}
                    className="flex items-center gap-2"
                  >
                    {dayData.enabled ? (
                      <ToggleRight className="w-8 h-8 text-[var(--primary)]" />
                    ) : (
                      <ToggleLeft className="w-8 h-8 text-[var(--text-muted)]" />
                    )}
                  </button>
                  <span className={`w-24 text-sm font-medium ${
                    dayData.enabled ? 'text-[var(--text-main)]' : 'text-[var(--text-muted)]'
                  }`}>
                    {day.label}
                  </span>
                  {dayData.enabled ? (
                    <div className="flex items-center gap-2 flex-1">
                      <select
                        value={dayData.startTime}
                        onChange={(e) => handleTimeChange(day.value, 'startTime', e.target.value)}
                        className="px-3 py-1.5 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm"
                      >
                        {TIME_OPTIONS.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      <span className="text-[var(--text-muted)]">to</span>
                      <select
                        value={dayData.endTime}
                        onChange={(e) => handleTimeChange(day.value, 'endTime', e.target.value)}
                        className="px-3 py-1.5 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm"
                      >
                        {TIME_OPTIONS.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <span className="text-sm text-[var(--text-muted)]">Unavailable</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Date Overrides */}
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[var(--text-main)]">Date Overrides</h2>
                <p className="text-sm text-[var(--text-muted)]">Block specific dates or set custom hours</p>
              </div>
            </div>
            <button
              onClick={() => setShowOverrideForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-element)] hover:bg-[var(--bg-hover)] rounded-lg text-sm font-medium text-[var(--text-main)] transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Override
            </button>
          </div>

          {showOverrideForm && (
            <div className="mb-6 p-4 bg-[var(--bg-main)] rounded-lg border border-[var(--border)]">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-main)] mb-1">Date</label>
                  <input
                    type="date"
                    value={overrideDate}
                    onChange={(e) => setOverrideDate(e.target.value)}
                    min={startDate}
                    className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-main)] mb-1">Type</label>
                  <select
                    value={overrideType}
                    onChange={(e) => setOverrideType(e.target.value as OverrideType)}
                    className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm"
                  >
                    <option value="unavailable">Unavailable (entire day)</option>
                    <option value="custom">Custom hours</option>
                  </select>
                </div>
              </div>
              {overrideType === 'custom' && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-main)] mb-1">Start</label>
                    <select
                      value={overrideStartTime}
                      onChange={(e) => setOverrideStartTime(e.target.value)}
                      className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm"
                    >
                      {TIME_OPTIONS.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-main)] mb-1">End</label>
                    <select
                      value={overrideEndTime}
                      onChange={(e) => setOverrideEndTime(e.target.value)}
                      className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm"
                    >
                      {TIME_OPTIONS.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[var(--text-main)] mb-1">Reason (optional)</label>
                <input
                  type="text"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="e.g., Vacation, Holiday"
                  className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowOverrideForm(false)}
                  className="px-4 py-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-main)]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddOverride}
                  disabled={!overrideDate}
                  className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
                >
                  Add Override
                </button>
              </div>
            </div>
          )}

          {overrides.length > 0 ? (
            <div className="space-y-2">
              {overrides.map(override => (
                <div
                  key={override.id}
                  className="flex items-center justify-between p-3 bg-[var(--bg-main)] rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      override.overrideType === 'unavailable' ? 'bg-red-500' : 'bg-yellow-500'
                    }`} />
                    <div>
                      <p className="text-sm font-medium text-[var(--text-main)]">
                        {new Date(override.overrideDate + 'T00:00:00').toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                      <p className="text-xs text-[var(--text-muted)]">
                        {override.overrideType === 'unavailable'
                          ? 'Unavailable'
                          : `${override.startTime} - ${override.endTime}`}
                        {override.reason && ` - ${override.reason}`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      removeOverride(override.id)
                      refetchOverrides()
                    }}
                    className="p-2 text-[var(--text-muted)] hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-[var(--text-muted)] py-4">
              No date overrides set
            </p>
          )}
        </div>

        {/* Booking Pages */}
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Link2 className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[var(--text-main)]">Booking Pages</h2>
                <p className="text-sm text-[var(--text-muted)]">Manage your public booking links</p>
              </div>
            </div>
            {bookingPages.length === 0 && (
              <button
                onClick={() => setShowBookingForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Booking Page
              </button>
            )}
          </div>

          {showBookingForm && (
            <div className="mb-6 p-4 bg-[var(--bg-main)] rounded-lg border border-[var(--border)]">
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-main)] mb-1">URL Slug</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[var(--text-muted)]">{window.location.origin}/book/</span>
                    <input
                      type="text"
                      value={bookingSlug}
                      onChange={(e) => setBookingSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder="your-name"
                      className="flex-1 px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-main)] mb-1">Title</label>
                  <input
                    type="text"
                    value={bookingTitle}
                    onChange={(e) => setBookingTitle(e.target.value)}
                    placeholder="Book a Meeting"
                    className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-main)] mb-1">Description (optional)</label>
                  <textarea
                    value={bookingDescription}
                    onChange={(e) => setBookingDescription(e.target.value)}
                    placeholder="Let's chat about your project..."
                    rows={2}
                    className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-main)] mb-1">Duration</label>
                  <select
                    value={bookingDuration}
                    onChange={(e) => setBookingDuration(Number(e.target.value))}
                    className="px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm"
                  >
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>60 minutes</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowBookingForm(false)}
                  className="px-4 py-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-main)]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateBookingPage}
                  disabled={!bookingSlug || savingPage}
                  className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
                >
                  {savingPage ? 'Creating...' : 'Create Page'}
                </button>
              </div>
            </div>
          )}

          {loadingPages ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-[var(--text-muted)]" />
            </div>
          ) : bookingPages.length > 0 ? (
            <div className="space-y-3">
              {bookingPages.map(page => (
                <div
                  key={page.id}
                  className="flex items-center justify-between p-4 bg-[var(--bg-main)] rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-[var(--text-main)]">{page.title}</h3>
                      {page.isActive ? (
                        <span className="px-2 py-0.5 bg-green-500/10 text-green-500 text-xs rounded-full">Active</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-[var(--bg-element)] text-[var(--text-muted)] text-xs rounded-full">Inactive</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-[var(--text-muted)]">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {page.defaultDuration} min
                      </span>
                      {page.allowVideoConference && (
                        <span className="flex items-center gap-1">
                          <Video className="w-3 h-3" />
                          Video
                        </span>
                      )}
                    </div>
                    <code className="text-xs text-[var(--primary)] mt-1 block truncate">
                      /book/{page.slug}
                    </code>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => copyLink(page.slug)}
                      className="p-2 hover:bg-[var(--bg-element)] rounded-lg transition-colors"
                      title="Copy link"
                    >
                      {copiedLink === page.slug ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 text-[var(--text-muted)]" />
                      )}
                    </button>
                    <a
                      href={getPublicLink(page.slug)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-[var(--bg-element)] rounded-lg transition-colors"
                      title="Preview"
                    >
                      <ExternalLink className="w-4 h-4 text-[var(--text-muted)]" />
                    </a>
                    <button
                      onClick={async () => {
                        await toggleBookingPage(page.id, !page.isActive)
                        refetchPages()
                      }}
                      className={`p-2 rounded-lg transition-colors ${
                        page.isActive
                          ? 'hover:bg-orange-500/10 text-orange-500'
                          : 'hover:bg-green-500/10 text-green-500'
                      }`}
                      title={page.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {page.isActive ? (
                        <ToggleRight className="w-4 h-4" />
                      ) : (
                        <ToggleLeft className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={async () => {
                        if (confirm('Delete this booking page?')) {
                          await deleteBookingPage(page.id)
                          refetchPages()
                        }
                      }}
                      className="p-2 hover:bg-red-500/10 text-[var(--text-muted)] hover:text-red-500 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 rounded-full bg-[var(--bg-element)] flex items-center justify-center mx-auto mb-3">
                <Link2 className="w-6 h-6 text-[var(--text-muted)]" />
              </div>
              <p className="text-[var(--text-muted)] mb-4">No booking pages yet</p>
              <button
                onClick={() => setShowBookingForm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Your First Booking Page
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PublicAvailabilityView
